/**
 * Reports storefront cart/checkout failures to PostHog and the Whisky Library backend
 * (POST /api/ops/cart-failure → Slack). Safe to call before PostHog SDK is ready.
 */
(function (window) {
  'use strict';

  var REPORTED = new WeakSet();
  var recentFingerprints = {};

  function markRecentlyReported(fp) {
    if (!fp) return;
    recentFingerprints[fp] = Date.now();
  }

  function recentlyReported(fp) {
    if (!fp) return false;
    var t = recentFingerprints[fp];
    if (!t) return false;
    if (Date.now() - t > 10000) {
      delete recentFingerprints[fp];
      return false;
    }
    return true;
  }

  function inferStage(errorMessage) {
    var msg = String(errorMessage || '').toLowerCase();
    if (/tier api|missing items|tier payload|no cart items/.test(msg)) return 'tier_fetch';
    if (/cart\/clear|clear\.js/.test(msg)) return 'cart_clear';
    if (/cart\/add|add\.js|cart add failed/.test(msg)) return 'cart_add';
    if (/empty|missing bundle|verify/.test(msg)) return 'cart_verify';
    return 'unknown';
  }

  function inferSource() {
    var path = window.location.pathname || '';
    if (document.querySelector('[data-mb-explorer-bundle-cta]')) return 'explorer_box_offer';
    if (document.querySelector('[data-mb-connoisseur-bundle-cta]')) return 'mystery_box_offer';
    if (document.getElementById('bourbon-subscription-form')) return 'replo_bourbon_subscription';
    if (path.indexOf('replo') !== -1 || document.querySelector('.alchemy__element')) return 'replo_landing';
    if (path.indexOf('taste-profile') !== -1) return 'taste_profile';
    if (path.indexOf('dragon-slayer') !== -1) return 'dragon_slayer';
    if (path.indexOf('father-day') !== -1) return 'father_day';
    if (path.indexOf('july-4') !== -1) return 'july_4_offer';
    if (path.indexOf('connoisseur-rebuttal') !== -1) return 'connoisseur_rebuttal';
    return 'storefront';
  }

  function getApiBase() {
    var base = typeof window.__QUIZ_API_BASE__ === 'string' ? window.__QUIZ_API_BASE__ : '';
    return String(base || '').replace(/\/$/, '');
  }

  function getOpsSecret() {
    return typeof window.__OPS_WEBHOOK_SECRET__ === 'string'
      ? window.__OPS_WEBHOOK_SECRET__.trim()
      : '';
  }

  function getQuizNavSource() {
    try {
      return sessionStorage.getItem('quiz_nav_source') || '';
    } catch (e) {
      return '';
    }
  }

  function getShopifyTemplate() {
    if (!window._template) return '';
    return [window._template.directory, window._template.name, window._template.suffix]
      .filter(Boolean)
      .join('.');
  }

  function generateCorrelationId() {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch (e) {}
    return 'cf_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }

  function getQuizSessionId() {
    try {
      return (window.localStorage.getItem('quiz_sessionId') || '').trim();
    } catch (e) {
      return '';
    }
  }

  function getTasteProfileResult() {
    var root = document.querySelector('.taste-profile-page');
    if (root) {
      var fromAttr = (root.getAttribute('data-taste-profile') || '').trim();
      if (fromAttr) return fromAttr;
    }
    var v3 = window.__TASTE_PROFILE_V3__;
    return (v3 && v3.tasteProfile ? String(v3.tasteProfile) : '').trim();
  }

  function getThemeRole() {
    try {
      return (
        (window.Shopify &&
          window.Shopify.theme &&
          window.Shopify.theme.role &&
          String(window.Shopify.theme.role)) ||
        ''
      );
    } catch (e) {
      return '';
    }
  }

  function getPosthogDistinctId() {
    try {
      if (window.posthog && typeof window.posthog.get_distinct_id === 'function') {
        return String(window.posthog.get_distinct_id() || '');
      }
    } catch (e) {}
    return '';
  }

  function getPosthogSessionId() {
    try {
      if (window.posthog && typeof window.posthog.get_session_id === 'function') {
        return String(window.posthog.get_session_id() || '');
      }
    } catch (e) {}
    return '';
  }

  function getDeviceContext() {
    var ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    var mobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
    var os = 'unknown';
    if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/Mac OS X/.test(ua)) os = 'macOS';
    else if (/Windows/.test(ua)) os = 'Windows';

    var browser = 'unknown';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/CriOS|Chrome\//.test(ua)) browser = mobile ? 'Mobile Chrome' : 'Chrome';
    else if (/FxiOS|Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = mobile ? 'Mobile Safari' : 'Safari';

    return {
      browser: browser,
      os: os,
      device_type: mobile ? 'Mobile' : 'Desktop',
    };
  }

  function inferErrorKind(errorMessage, httpStatus, stage) {
    if (httpStatus != null && Number.isFinite(Number(httpStatus))) return 'http';
    var msg = String(errorMessage || '').toLowerCase();
    if (/load failed|failed to fetch|networkerror|network error|aborted|the operation was aborted/.test(msg)) {
      return 'network';
    }
    if (/\.js failed: \d+|\/cart\/[\w.]+\.js \d{3}/.test(msg)) return 'http';
    if (stage === 'tier_fetch') return 'application';
    return 'application';
  }

  function inferCartEndpoint(errorMessage, stage, explicitEndpoint) {
    if (explicitEndpoint) return String(explicitEndpoint);
    var msg = String(errorMessage || '');
    var match = msg.match(/\/cart\/[\w.]+\.js/);
    if (match) return match[0];
    if (stage === 'cart_clear') return '/cart/clear.js';
    if (stage === 'cart_add') return '/cart/add.js';
    if (stage === 'cart_verify') return '/cart.js';
    return '';
  }

  function funnelContext(extra) {
    var w = window.whiskyPosthog;
    if (w && typeof w.funnelContext === 'function') {
      return w.funnelContext(extra || {});
    }
    var ctx = {
      quiz_session_id: getQuizSessionId(),
      taste_profile_result: getTasteProfileResult(),
      shopify_template: window._template
        ? [window._template.directory, window._template.name, window._template.suffix]
            .filter(Boolean)
            .join('.')
        : undefined,
      page_path: window.location.pathname,
      page_url: window.location.href,
    };
    if (extra && typeof extra === 'object') {
      Object.keys(extra).forEach(function (key) {
        ctx[key] = extra[key];
      });
    }
    return ctx;
  }

  function normalizeError(err) {
    if (err instanceof Error) return err.message || String(err);
    if (typeof err === 'string') return err;
    try {
      return JSON.stringify(err);
    } catch (e) {
      return String(err);
    }
  }

  function fingerprint(ctx) {
    var parts = [
      ctx.source || '',
      ctx.stage || '',
      ctx.http_status != null ? String(ctx.http_status) : '',
      normalizeError(ctx.error_message || '')
        .toLowerCase()
        .replace(/\d{4,}/g, 'N'),
      ctx.tier_key || '',
    ];
    var raw = parts.join('|');
    var hash = 0;
    for (var i = 0; i < raw.length; i++) {
      hash = (hash << 5) - hash + raw.charCodeAt(i);
      hash |= 0;
    }
    return 'fp_' + Math.abs(hash).toString(36);
  }

  function capturePosthog(ctx) {
    var props = funnelContext({
      source: ctx.source,
      stage: ctx.stage,
      error_message: normalizeError(ctx.error_message),
      http_status: ctx.http_status,
      shopify_response: ctx.shopify_response,
      variant_ids: ctx.variant_ids,
      tier_key: ctx.tier_key,
      funnel_mode: ctx.funnel_mode,
      failure_fingerprint: ctx.failure_fingerprint,
      cart_item_count: ctx.cart_item_count,
      posthog_distinct_id: ctx.posthog_distinct_id,
      posthog_session_id: ctx.posthog_session_id,
      correlation_id: ctx.correlation_id,
      theme_role: ctx.theme_role,
      quiz_nav_source: ctx.quiz_nav_source,
      error_kind: ctx.error_kind,
      cart_endpoint: ctx.cart_endpoint,
      browser: ctx.browser,
      os: ctx.os,
      device_type: ctx.device_type,
      screen_viewport: ctx.screen_viewport,
    });
    if (window.whiskyPosthog && typeof window.whiskyPosthog.capture === 'function') {
      window.whiskyPosthog.capture('cart_checkout_failed', props);
      return;
    }
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('cart_checkout_failed', props);
    }
  }

  function postToBackend(ctx) {
    var base = getApiBase();
    var secret = getOpsSecret();
    if (!base || !secret) return;

    var body = {
      source: ctx.source,
      stage: ctx.stage,
      error_message: normalizeError(ctx.error_message),
      http_status: ctx.http_status,
      shopify_response: ctx.shopify_response,
      variant_ids: ctx.variant_ids,
      tier_key: ctx.tier_key,
      funnel_mode: ctx.funnel_mode,
      page_url: ctx.page_url || window.location.href,
      page_path: ctx.page_path || window.location.pathname,
      page_referrer: ctx.page_referrer || (typeof document !== 'undefined' ? document.referrer : ''),
      shopify_template: ctx.shopify_template || getShopifyTemplate(),
      quiz_session_id: ctx.quiz_session_id || getQuizSessionId(),
      quiz_nav_source: ctx.quiz_nav_source || getQuizNavSource(),
      taste_profile_result: ctx.taste_profile_result || getTasteProfileResult(),
      cart_item_count: ctx.cart_item_count,
      posthog_distinct_id: ctx.posthog_distinct_id,
      posthog_session_id: ctx.posthog_session_id,
      correlation_id: ctx.correlation_id,
      failure_fingerprint: ctx.failure_fingerprint,
      reported_at: ctx.reported_at || new Date().toISOString(),
      user_agent: ctx.user_agent || navigator.userAgent,
      screen_viewport:
        ctx.screen_viewport ||
        (typeof window !== 'undefined'
          ? String(window.innerWidth || '') + 'x' + String(window.innerHeight || '')
          : ''),
      theme_role: ctx.theme_role || getThemeRole(),
      error_kind: ctx.error_kind,
      cart_endpoint: ctx.cart_endpoint,
      browser: ctx.browser,
      os: ctx.os,
      device_type: ctx.device_type,
    };

    var url = base + '/api/ops/cart-failure';
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-ops-secret': secret,
      },
      body: JSON.stringify(body),
      credentials: 'omit',
      keepalive: true,
    }).catch(function () {});
  }

  /**
   * @param {object} ctx
   * @param {string} ctx.source
   * @param {string} ctx.stage
   * @param {string|Error} ctx.error_message
   */
  function report(ctx) {
    if (!ctx || !ctx.source || !ctx.stage) return;
    if (ctx.error && !ctx.error_message) {
      ctx.error_message = ctx.error;
    }

    var correlationId = ctx.correlation_id || generateCorrelationId();
    var stage = ctx.stage || inferStage(ctx.error_message);
    var errorMessage = normalizeError(ctx.error_message || 'Unknown cart failure');
    var device = getDeviceContext();
    var screenViewport =
      ctx.screen_viewport ||
      (typeof window !== 'undefined'
        ? String(window.innerWidth || '') + 'x' + String(window.innerHeight || '')
        : '');

    var payload = Object.assign({}, ctx, {
      error_message: errorMessage,
      correlation_id: correlationId,
      posthog_distinct_id: ctx.posthog_distinct_id || getPosthogDistinctId(),
      posthog_session_id: ctx.posthog_session_id || getPosthogSessionId(),
      failure_fingerprint: ctx.failure_fingerprint || fingerprint(ctx),
      theme_role: ctx.theme_role || getThemeRole(),
      stage: stage,
      shopify_template: ctx.shopify_template || getShopifyTemplate(),
      quiz_nav_source: ctx.quiz_nav_source || getQuizNavSource(),
      reported_at: ctx.reported_at || new Date().toISOString(),
      browser: ctx.browser || device.browser,
      os: ctx.os || device.os,
      device_type: ctx.device_type || device.device_type,
      screen_viewport: screenViewport,
      error_kind: ctx.error_kind || inferErrorKind(errorMessage, ctx.http_status, stage),
      cart_endpoint: inferCartEndpoint(errorMessage, stage, ctx.cart_endpoint),
    });

    if (recentlyReported(payload.failure_fingerprint)) return;

    if (ctx._reportToken && REPORTED.has(ctx._reportToken)) return;
    if (ctx._reportToken) REPORTED.add(ctx._reportToken);

    capturePosthog(payload);
    postToBackend(payload);
    markRecentlyReported(payload.failure_fingerprint);

    if (payload.source && payload.stage) {
      try {
        var legacyEvent = payload.source.replace(/-/g, '_') + '_checkout_failed';
        if (window.whiskyPosthog && typeof window.whiskyPosthog.capture === 'function') {
          window.whiskyPosthog.capture(
            legacyEvent,
            funnelContext({ error_message: payload.error_message, tier_key: payload.tier_key }),
          );
        }
      } catch (e) {}
    }
  }

  function installFetchInterceptor() {
    if (window.__CART_FAILURE_FETCH_PATCHED__) return;
    window.__CART_FAILURE_FETCH_PATCHED__ = true;

    var native = window.fetch.bind(window);
    if (!window.__TP_ORIG_FETCH__) {
      window.__TP_ORIG_FETCH__ = native;
    }

    window.fetch = function cartFailureAwareFetch(input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var method = (init && init.method) || (input && input.method) || 'GET';

      return native(input, init).then(function (res) {
        try {
          if (
            res &&
            !res.ok &&
            /\/cart\/(add|clear)\.js/i.test(url) &&
            String(method).toUpperCase() === 'POST'
          ) {
            var stage = /clear\.js/i.test(url) ? 'cart_clear' : 'cart_add';
            res.clone()
              .text()
              .catch(function () {
                return '';
              })
              .then(function (bodyText) {
                var errMsg =
                  '/cart/' +
                  (stage === 'cart_clear' ? 'clear' : 'add') +
                  '.js ' +
                  res.status +
                  (bodyText ? ' ' + bodyText.slice(0, 400) : '');
                var fp = fingerprint({
                  source: inferSource(),
                  stage: stage,
                  http_status: res.status,
                  error_message: errMsg,
                });
                if (!recentlyReported(fp)) {
                  report({
                    source: inferSource(),
                    stage: stage,
                    error_message: errMsg,
                    http_status: res.status,
                    shopify_response: bodyText ? bodyText.slice(0, 400) : undefined,
                    funnel_mode: 'fetch_intercept',
                  });
                }
              });
          }
        } catch (e) {}
        return res;
      });
    };
  }

  installFetchInterceptor();

  window.WhiskyCartFailure = {
    report: report,
    fingerprint: fingerprint,
    inferStage: inferStage,
    inferSource: inferSource,
  };
})(window);
